// reducers/quoteReducer.js
import { addDays } from 'date-fns';

// Add this helper function at the top
const transformApiItemsToInternal = (apiItems = []) => {
    return apiItems.map((item, index) => ({
        id: item.id || `item-${index}`,
        productcode: item.product_code || item.productcode || '',
        description: item.description || '',
        unitprice: parseFloat(item.unit_price || item.unitprice || 0),
        quantity: parseFloat(item.quantity || 1),
        unit: item.unit || 'st',
        tax_rate: item.tax_rate || '25%',
        discount: parseFloat(item.discount || 0),
        type: item.type || 'Product',
        item_id: item.id, // Keep original API ID for updates
        isNew: false // Mark as existing item
    }));
};

// Action types
export const INVOICE_ACTIONS = {
    UPDATE_INVOICE_DATA: 'UPDATE_INVOICE_DATA',
    SET_CUSTOMER: 'SET_CUSTOMER',
    SET_ITEMS: 'SET_ITEMS',
    UPDATE_ITEM: 'UPDATE_ITEM',
    ADD_ITEM: 'ADD_ITEM',
    REMOVE_ITEM: 'REMOVE_ITEM',
    UPDATE_DELIVERY_OPTIONS: 'UPDATE_DELIVERY_OPTIONS',
    TOGGLE_PAYMENT_METHOD: 'TOGGLE_PAYMENT_METHOD',
    SET_TEMPLATE: 'SET_TEMPLATE',
    LOAD_INVOICE: 'LOAD_INVOICE',
    RESET: 'RESET',
    // Address and contact selection
    SET_SELECTED_ADDRESS: 'SET_SELECTED_ADDRESS',
    SET_SELECTED_CONTACT: 'SET_SELECTED_CONTACT',
    SET_ADDRESS_OVERRIDE: 'SET_ADDRESS_OVERRIDE',
    SET_CONTACT_OVERRIDE: 'SET_CONTACT_OVERRIDE',
    RESET_ADDRESS_OVERRIDE: 'RESET_ADDRESS_OVERRIDE',
    RESET_CONTACT_OVERRIDE: 'RESET_CONTACT_OVERRIDE'
};

// Initial state factory
export const createInitialState = (invoiceId = null, isEditMode = false, invoiceSettings = {}) => {
    // Extract payment methods that are enabled
    const enabledPaymentMethods = {};
    if (invoiceSettings?.paymentMethods) {
        const methods = ['bankgiro', 'plusgiro', 'paypal', 'swish'];
        methods.forEach(method => {
            enabledPaymentMethods[method] = invoiceSettings.paymentMethods[method] === true;
        });
    }

    // Get default delivery method
    const defaultDeliveryMethod = invoiceSettings?.deliveryChannels?.defaultChannel || 'download';

    // Get default VAT rate
    const defaultVatRate = invoiceSettings?.taxSettings?.defaultVatRate || 25;

    // Get default payment terms
    const paymentDays = invoiceSettings?.defaultTerms?.paymentDays || 30;

    const template = invoiceSettings?.template?.defaultTemplate || 'classic';

    return {
        // Customer info
        customerid: '',
        customer: null,

        // Quote details
        invoiceNumber: '',
        invoiceDate: new Date(),
        dueDate: addDays(new Date(), paymentDays),

        // Financial settings
        currency: 'SEK',
        roundTotal: false,
        defaultVatRate: defaultVatRate,

        // Content
        items: [],
        notes: '',
        attachments: [],

        // Status
        status: 'Draft',

        // Delivery options - initialized from settings
        deliveryOptions: {
            method: defaultDeliveryMethod,
            email: '',
            phone: '',
            message: '',
            subject: 'Faktura',
        },

        // Payment methods - initialized from settings
        paymentMethods: enabledPaymentMethods,

        // Template - initialized from settings
        selectedTemplate: template,

        // Address and contact selection (for invoices)
        selectedAddressId: null,      // Which address is selected (null = use default)
        selectedContactId: null,      // Which contact is selected (null = use default)
        invoiceAddressOverride: null, // One-time address edits for this invoice
        invoiceContactOverride: null, // One-time contact edits for this invoice

        // Accounting method override (periodisera)
        use_accrual_method: false,

        // VAT treatment (Session C). Auto-derived from customer + tenant
        // vat_status when a customer is picked. Read-only display in the
        // form; flows through to invoice.vat_treatment on save.
        vat_treatment: 'taxable',
    };
};

// Quote reducer
export const invoiceReducer = (state, action) => {
    switch (action.type) {
        case INVOICE_ACTIONS.UPDATE_INVOICE_DATA:
            return {
                ...state,
                ...action.payload
            };

        case INVOICE_ACTIONS.SET_CUSTOMER:
            console.log('REDUCER: Setting customer, current payment methods:', state.paymentMethods);
            // Use customer-specific payment terms if available, unless due date should be preserved (e.g. loading existing invoice)
            const customerPaymentDays = action.payload?.payment_terms;
            const customerDueDate = (!action.preserveDueDate && customerPaymentDays)
                ? addDays(state.invoiceDate || new Date(), Number(customerPaymentDays))
                : state.dueDate;
            const customerState = {
                ...state,
                customer: action.payload,
                customerid: action.payload?.id || '',
                dueDate: customerDueDate,
                // Update delivery email if customer has email, but preserve other delivery options
                deliveryOptions: {
                    ...state.deliveryOptions,
                    email: action.payload?.email || state.deliveryOptions.email
                },
                // Reset address/contact selections when customer changes
                selectedAddressId: null,
                selectedContactId: null,
                invoiceAddressOverride: null,
                invoiceContactOverride: null
                // DON'T touch payment methods here
            };
            console.log('REDUCER: After setting customer, payment methods:', customerState.paymentMethods);
            return customerState;

        case INVOICE_ACTIONS.SET_ITEMS:
            return {
                ...state,
                items: action.payload
            };

        case INVOICE_ACTIONS.UPDATE_ITEM:
            return {
                ...state,
                items: state.items.map((item, idx) =>
                    idx === action.index ? action.payload : item
                )
            };

        case INVOICE_ACTIONS.ADD_ITEM:
            return {
                ...state,
                items: [...state.items, action.payload]
            };

        case INVOICE_ACTIONS.REMOVE_ITEM:
            return {
                ...state,
                items: state.items.filter((_, idx) => idx !== action.index)
            };

        case INVOICE_ACTIONS.UPDATE_DELIVERY_OPTIONS:
            return {
                ...state,
                deliveryOptions: {
                    ...state.deliveryOptions,
                    ...action.payload
                }
            };

        case INVOICE_ACTIONS.TOGGLE_PAYMENT_METHOD:
            return {
                ...state,
                paymentMethods: {
                    ...state.paymentMethods,
                    [action.payload]: !state.paymentMethods[action.payload]
                }
            };

        case INVOICE_ACTIONS.SET_TEMPLATE:
            return {
                ...state,
                selectedTemplate: action.payload
            };

        case INVOICE_ACTIONS.LOAD_INVOICE:
            const loadedInvoice = action.payload;
            const invoiceSettings = action.invoiceSettings || {};
            console.log('REDUCER: Loading invoice with payment methods:', loadedInvoice.paymentMethods);

            // Use invoice settings for fallback payment days
            const defaultPaymentDays = invoiceSettings?.defaultTerms?.paymentDays || 30;

            const newState = {
                ...state,
                // Use the already transformed data from fetchInvoiceDetails
                invoiceId: loadedInvoice.invoiceId,
                customerid: loadedInvoice.customerid || '',
                customer: loadedInvoice.customer || null,
                invoiceNumber: loadedInvoice.invoiceNumber || '',
                invoiceDate: loadedInvoice.invoiceDate || new Date(),
                dueDate: loadedInvoice.dueDate || addDays(new Date(), defaultPaymentDays),
                currency: loadedInvoice.currency || 'SEK',
                roundTotal: loadedInvoice.roundTotal || false,
                notes: loadedInvoice.notes || '',
                status: loadedInvoice.status || 'Draft',

                // Use the already transformed items
                items: loadedInvoice.items || [],

                // Preserve delivery options structure
                deliveryOptions: {
                    ...state.deliveryOptions,
                    ...loadedInvoice.deliveryOptions
                },

                // Use loaded payment methods completely, don't merge with state
                paymentMethods: loadedInvoice.paymentMethods || {},

                selectedTemplate: loadedInvoice.selectedTemplate || state.selectedTemplate,

                // Restore address/contact selections if saved with invoice
                selectedAddressId: loadedInvoice.selectedAddressId || null,
                selectedContactId: loadedInvoice.selectedContactId || null,
                invoiceAddressOverride: loadedInvoice.invoiceAddressOverride || null,
                invoiceContactOverride: loadedInvoice.invoiceContactOverride || null,

                // Accounting method override
                use_accrual_method: Boolean(loadedInvoice.use_accrual_method),

                // VAT treatment (Session C)
                vat_treatment: loadedInvoice.vat_treatment || 'taxable'
            };
            
            console.log('REDUCER: New state payment methods:', newState.paymentMethods);
            return newState;

        case INVOICE_ACTIONS.RESET:
            return action.payload || createInitialState();

        // Address and contact selection actions
        case INVOICE_ACTIONS.SET_SELECTED_ADDRESS:
            return {
                ...state,
                selectedAddressId: action.payload,
                // Clear override when selecting a different address
                invoiceAddressOverride: null
            };

        case INVOICE_ACTIONS.SET_SELECTED_CONTACT:
            return {
                ...state,
                selectedContactId: action.payload,
                // Clear override when selecting a different contact
                invoiceContactOverride: null
            };

        case INVOICE_ACTIONS.SET_ADDRESS_OVERRIDE:
            return {
                ...state,
                invoiceAddressOverride: action.payload
            };

        case INVOICE_ACTIONS.SET_CONTACT_OVERRIDE:
            return {
                ...state,
                invoiceContactOverride: action.payload
            };

        case INVOICE_ACTIONS.RESET_ADDRESS_OVERRIDE:
            return {
                ...state,
                invoiceAddressOverride: null
            };

        case INVOICE_ACTIONS.RESET_CONTACT_OVERRIDE:
            return {
                ...state,
                invoiceContactOverride: null
            };

        default:
            return state;
    }
};